import { useMemo, useState, useCallback } from 'react';
import { orderBy, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { AlertTriangle, BellOff, Check, Clock, RefreshCw, ShieldAlert, Smartphone } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import EstadoMonitoreoBanner from '../../components/costos/EstadoMonitoreoBanner';
import SesionesLivekitSeccion from '../../components/costos/SesionesLivekitSeccion';
import { useCollection } from '../../hooks/useFirestore';
import styles from './AlertasPage.module.css';

/**
 * Tablero de alertas.
 *
 * El monitor de eventos muestra TODO lo que pasa; esto muestra solo lo que hay
 * que atender. La diferencia está en el ciclo de vida: una alerta se cierra
 * sola cuando deja de ocurrir, así que lo que aparece aquí está vivo por
 * definición y no hace falta preguntarse si ya se arregló.
 */
interface Alerta {
  id: string;
  codigo?: string;
  categoria?: string;
  componente?: string;
  nivel?: 'WARN' | 'ERROR' | 'FATAL';
  /** De qué sistema viene: el portal o la app del paciente. */
  origen?: 'backend' | 'frontend' | 'movil';
  estado?: 'abierta' | 'reconocida' | 'resuelta' | 'silenciada';
  ocurrencias?: number;
  reaperturas?: number;
  mensajeEjemplo?: string;
  consultasAfectadas?: string[];
  primeraOcurrencia?: { toDate?: () => Date };
  ultimaOcurrencia?: { toDate?: () => Date };
  resueltaEn?: { toDate?: () => Date };
  resueltaPor?: string;
  /** Dejó de ocurrir, pero el sistema no se ha vuelto a usar lo suficiente
   *  como para dar por hecho que se corrigió. */
  esperandoActividad?: boolean;
  consultasDesdeUltima?: number;
}

const hace = (d?: Date) => {
  if (!d) return '—';
  const min = Math.round((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'ahora mismo';
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  return h < 24 ? `hace ${h} h` : `hace ${Math.round(h / 24)} d`;
};

const hora = (d?: Date) =>
  d ? d.toLocaleString('es-PA', { timeZone: 'America/Panama', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

export default function AlertasPage() {
  const { data: alertas, loading } = useCollection<Alerta>('alertas', [orderBy('ultimaOcurrencia', 'desc'), limit(200)]);
  const [ocupada, setOcupada] = useState<string | null>(null);
  const [verResueltas, setVerResueltas] = useState(false);

  const accion = useCallback(async (alertaId: string, acc: string) => {
    setOcupada(alertaId);
    try {
      await httpsCallable(functions, 'portal_gestionarAlerta')({ alertaId, accion: acc });
    } catch (e) {
      console.error('[alertas]', e);
    } finally {
      setOcupada(null);
    }
  }, []);

  const { activas, resueltas } = useMemo(() => {
    const act = (alertas ?? []).filter((a) => a.estado === 'abierta' || a.estado === 'reconocida');
    const res = (alertas ?? []).filter((a) => a.estado === 'resuelta' || a.estado === 'silenciada');
    /* Lo más grave primero, y a igual gravedad lo más repetido: es el orden en
       el que conviene atacarlas, no el cronológico. */
    const peso = { FATAL: 3, ERROR: 2, WARN: 1 } as Record<string, number>;
    act.sort((x, y) =>
      (peso[y.nivel ?? 'WARN'] ?? 0) - (peso[x.nivel ?? 'WARN'] ?? 0) ||
      (y.ocurrencias ?? 0) - (x.ocurrencias ?? 0));
    return { activas: act, resueltas: res };
  }, [alertas]);

  const tarjeta = (a: Alerta, activa: boolean) => (
    <div key={a.id} className={`${styles.card} ${styles[`n${a.nivel ?? 'WARN'}`]}`}>
      <div className={styles.cardHead}>
        <span className={`${styles.pill} ${styles[`p${a.nivel ?? 'WARN'}`]}`}>{a.nivel}</span>
        <span className={styles.codigo}>{a.codigo ?? a.id}</span>
        {/* Solo se marca el móvil: en un tablero del portal, que algo venga del
            portal es lo esperado y no gana nada por decirlo. Lo que cambia la
            reacción es que venga de la app — otro repositorio, otro despliegue,
            y un paciente del otro lado en vez de un médico. */}
        {a.origen === 'movil' && (
          <span className={styles.movil} title="Ocurrió en la app del paciente">
            <Smartphone size={11} aria-hidden="true" /> App móvil
          </span>
        )}
        <span className={styles.veces} title="Veces que ha ocurrido en total">
          {a.ocurrencias ?? 0}×
        </span>
        {(a.reaperturas ?? 0) > 0 && (
          <span className={styles.reincidente} title="Se cerró y volvió a ocurrir">
            <RefreshCw size={11} aria-hidden="true" /> reincidente {a.reaperturas}
          </span>
        )}
      </div>

      <p className={styles.mensaje}>{a.mensajeEjemplo || 'Sin mensaje de ejemplo'}</p>

      {activa && a.esperandoActividad && (
        /* Sin esto, al día siguiente se ve una alerta vieja sin repeticiones y
           no se sabe si el problema sigue o simplemente nadie usó la app. */
        <div className={styles.enEspera}>
          <Clock size={12} aria-hidden="true" />
          <span>
            No se ha repetido, pero <strong>tampoco ha vuelto a usarse el sistema</strong> lo
            suficiente para darla por corregida
            {typeof a.consultasDesdeUltima === 'number' ? ` (${a.consultasDesdeUltima} consulta(s) desde entonces)` : ''}.
            Se cerrará sola cuando haya actividad y no reaparezca.
          </span>
        </div>
      )}

      <div className={styles.meta}>
        <span>{a.categoria} · {a.componente}</span>
        {activa
          ? <span><Clock size={11} aria-hidden="true" /> última {hace(a.ultimaOcurrencia?.toDate?.())}</span>
          : <span><Check size={11} aria-hidden="true" /> resuelta {hace(a.resueltaEn?.toDate?.())}
              {a.resueltaPor === 'auto' ? ' · no volvió a ocurrir con el sistema en uso' : ''}
              {a.resueltaPor === 'auto_caducada' ? ' · 7 días sin repetirse' : ''}</span>}
        <span>desde {hora(a.primeraOcurrencia?.toDate?.())}</span>
      </div>

      {!!a.consultasAfectadas?.length && (
        <div className={styles.consultas}>
          {a.consultasAfectadas.slice(0, 3).map((c) => (
            <a key={c} href={`/consulta/${c}/video-ai`} className={styles.consultaLink}>{c.slice(0, 22)}…</a>
          ))}
          {a.consultasAfectadas.length > 3 && <span>+{a.consultasAfectadas.length - 3}</span>}
        </div>
      )}

      {activa && (
        <div className={styles.acciones}>
          {a.estado !== 'reconocida' && (
            <button type="button" onClick={() => accion(a.id, 'reconocer')} disabled={ocupada === a.id}>
              <ShieldAlert size={13} aria-hidden="true" /> La estoy viendo
            </button>
          )}
          <button type="button" onClick={() => accion(a.id, 'resolver')} disabled={ocupada === a.id}>
            <Check size={13} aria-hidden="true" /> Resolver
          </button>
          <button type="button" onClick={() => accion(a.id, 'silenciar')} disabled={ocupada === a.id}>
            <BellOff size={13} aria-hidden="true" /> Silenciar 24 h
          </button>
        </div>
      )}
    </div>
  );

  return (
    <MainLayout pageTitle="Alertas" breadcrumb="Administración > Alertas">
      <div className={styles.wrap}>
        <EstadoMonitoreoBanner compacto />

        <div className={styles.explicacion}>
          <AlertTriangle size={16} aria-hidden="true" />
          <span>
            Una alerta agrupa todas las veces que ha ocurrido un mismo error y{' '}
            <strong>se cierra sola cuando el sistema se vuelve a usar y no reaparece</strong>.
            No basta con que pase el tiempo: fuera del horario de atención los errores dejan de
            salir porque nadie está usando la app, no porque se hayan corregido.
          </span>
        </div>

        {loading ? (
          <p className={styles.vacio}>Cargando…</p>
        ) : activas.length === 0 ? (
          <div className={styles.todoBien}>
            <Check size={20} aria-hidden="true" />
            <div>
              <strong>Ninguna alerta activa</strong>
              <p>No hay errores repitiéndose en este momento.</p>
            </div>
          </div>
        ) : (
          <div className={styles.grid}>{activas.map((a) => tarjeta(a, true))}</div>
        )}

        <SesionesLivekitSeccion />

        <button type="button" className={styles.toggle} onClick={() => setVerResueltas((v) => !v)}>
          {verResueltas ? 'Ocultar' : 'Ver'} resueltas y silenciadas ({resueltas.length})
        </button>
        {verResueltas && <div className={styles.grid}>{resueltas.map((a) => tarjeta(a, false))}</div>}
      </div>
    </MainLayout>
  );
}
